import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Calendar, Sparkles, ArrowRight, Heart, Clock, Star } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const categoryColors = {
  indigo: { bg: "bg-indigo-500", light: "bg-indigo-50 text-indigo-700", border: "border-indigo-200" },
  purple: { bg: "bg-purple-500", light: "bg-purple-50 text-purple-700", border: "border-purple-200" },
  teal: { bg: "bg-teal-500", light: "bg-teal-50 text-teal-700", border: "border-teal-200" },
  amber: { bg: "bg-amber-500", light: "bg-amber-50 text-amber-700", border: "border-amber-200" },
  rose: { bg: "bg-rose-500", light: "bg-rose-50 text-rose-700", border: "border-rose-200" },
  pink: { bg: "bg-pink-500", light: "bg-pink-50 text-pink-700", border: "border-pink-200" },
  stone: { bg: "bg-stone-500", light: "bg-stone-50 text-stone-700", border: "border-stone-200" },
};

const KarineDailyBlog = () => {
  const { language } = useLanguage();
  const isFr = language === "fr";
  const [expanded, setExpanded] = useState(false);
  const [tip, setTip] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTip = async () => {
      try {
        const res = await axios.get(`${API}/daily-tip/today`);
        if (res.data.success) {
          setTip(res.data);
        }
      } catch (err) {
        console.error("Failed to fetch daily tip:", err);
      }
      setLoading(false);
    };
    fetchTip();
  }, []);

  const formatDate = () => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return new Date().toLocaleDateString(isFr ? 'fr-CA' : 'en-CA', options);
  };

  if (loading) {
    return (
      <section className="py-12 md:py-16 bg-gradient-to-br from-stone-50 via-amber-50/30 to-rose-50/30">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="animate-pulse">
            <div className="h-6 bg-stone-200 rounded w-48 mx-auto mb-4"></div>
            <div className="h-4 bg-stone-200 rounded w-32 mx-auto"></div>
          </div>
        </div>
      </section>
    );
  }

  if (!tip) return null;

  const colors = categoryColors[tip.category_color] || categoryColors.stone;
  
  // Split text into paragraphs for rendering
  const paragraphs = (tip.text || "").split("\n").filter(p => p.trim());
  const previewParagraphs = paragraphs.slice(0, 3);
  const hasMore = paragraphs.length > 3;

  return (
    <section className="py-12 md:py-16 bg-gradient-to-br from-stone-50 via-amber-50/30 to-rose-50/30" data-testid="daily-blog-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full mb-4">
            <Sparkles className="w-4 h-4 text-amber-600" />
            <span className="text-amber-700 text-xs font-semibold uppercase tracking-wider">
              {isFr ? "Le Conseil du Jour de Karine" : "Karine's 1 Minute Skin Tip"}
            </span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
            {isFr ? "Conseil Beauté du Jour" : "Today's Beauty Insight"}
          </h2>
          <p className="text-stone-500 text-sm flex items-center justify-center gap-2">
            <Calendar className="w-4 h-4" />
            {tip.date || formatDate()}
          </p>
        </div>

        {/* Tip Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`bg-white rounded-2xl shadow-lg border ${colors.border} overflow-hidden`}
        >
          {/* Category + Title Bar */}
          <div className="p-5 sm:p-6 pb-0">
            <div className="flex items-center gap-3 mb-4">
              <span className={`${colors.light} text-xs font-semibold px-3 py-1 rounded-full`}>
                {tip.category}
              </span>
              <div className="flex items-center gap-1 text-stone-400 text-xs">
                <Clock className="w-3 h-3" />
                1 min read
              </div>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-stone-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              {tip.title}
            </h3>
          </div>

          {/* Content */}
          <div className="px-5 sm:px-6 pb-5 sm:pb-6">
            <div className="prose prose-stone max-w-none text-stone-600 text-sm sm:text-base leading-relaxed">
              {(expanded ? paragraphs : previewParagraphs).map((para, i) => {
                // Handle bold text with **
                const parts = para.split(/(\*\*.*?\*\*)/g);
                return (
                  <p key={i} className="mb-3">
                    {parts.map((part, j) => {
                      if (part.startsWith("**") && part.endsWith("**")) {
                        return <strong key={j} className="text-stone-800">{part.slice(2, -2)}</strong>;
                      }
                      // Handle bullet points
                      if (part.trim().startsWith("•") || part.trim().startsWith("-")) {
                        return <span key={j} className="block ml-4">{part}</span>;
                      }
                      return <span key={j}>{part}</span>;
                    })}
                  </p>
                );
              })}
            </div>

            {hasMore && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="mt-2 text-amber-600 hover:text-amber-700 text-sm font-semibold flex items-center gap-1 transition-colors"
                data-testid="tip-read-more"
              >
                {expanded 
                  ? (isFr ? "Voir moins" : "Read less") 
                  : (isFr ? "Lire la suite..." : "Continue reading...")}
                <ArrowRight className={`w-4 h-4 transition-transform ${expanded ? "rotate-90" : ""}`} />
              </button>
            )}
          </div>

          {/* Footer */}
          <div className="bg-stone-50 border-t border-stone-100 px-5 sm:px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-stone-500 text-xs">
              <Star className="w-4 h-4 text-amber-500" />
              {isFr 
                ? `Conseil ${tip.tip_index + 1} sur ${tip.total_tips} \u2022 Nouveau conseil chaque jour!`
                : `Tip ${tip.tip_index + 1} of ${tip.total_tips} \u2022 New tip every day!`
              }
            </div>
            <Link to="/beauty-hub">
              <Button variant="ghost" size="sm" className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 text-xs">
                <Heart className="w-3.5 h-3.5 mr-1.5" />
                {isFr ? "Voir tous les conseils" : "View all tips"}
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default KarineDailyBlog;
